import React, { useState } from 'react';
import * as I from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './UI';

const Sidebar = ({ navigate, currentPage }) => {
    const { logout, user } = useAuth();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState(new Set());
    const [hoveredMenu, setHoveredMenu] = useState(null);

    const navItems = [
        { id: 'dashboard', label: 'Dashboard', icon: I.LayoutDashboard, roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR'] },
        { id: 'invoices', label: 'Invoice Management', icon: I.Search, roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR'] },
        { id: 'vendors', label: 'Vendor Management', icon: I.Building2, roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR'] },
        { id: 'approvals', label: 'Approvals', icon: I.CheckCircle, roles: ['ADMIN'] },
        {
            id: 'payments',
            label: 'Payments',
            icon: I.Banknote,
            roles: ['ADMIN', 'PAYABLES_EXECUTIVE'],
            subItems: [
                { id: 'payments', label: 'Invoices to Pay', icon: I.FileText },
                { id: 'payment-batches', label: 'Payment Batches', icon: I.Layers }
            ]
        },
        {
            id: 'tds',
            label: 'TDS Management',
            icon: I.IndianRupee,
            roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR'],
            subItems: [
                { id: 'tds-invoices', label: 'TDS Invoices', icon: I.FileText },
                { id: 'tds-batches', label: 'TDS Batches', icon: I.Package },
                { id: 'invoice-excel-view', label: 'Excel View', icon: I.Table },
                { id: 'annual-audit', label: 'Annual Audit', icon: I.Archive }
            ]
        },
    ];

    const filteredNavItems = navItems.filter(item =>
        item.roles.some(role => user?.roles?.includes(role))
    );

    const toggleMenu = (menuId) => {
        setExpandedMenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(menuId)) {
                newSet.delete(menuId);
            } else {
                newSet.clear(); // Accordion mode: close others
                newSet.add(menuId);
            }
            return newSet;
        });
    };

    const isMenuExpanded = (menuId) => expandedMenus.has(menuId);

    return (
        <>
            {/* Mobile Overlay */}
            {!isCollapsed && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setIsCollapsed(true)}
                />
            )}

            <aside className={`
                ${isCollapsed ? 'w-16' : 'w-64'}
                bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 text-blue-100 flex flex-col shrink-0 transition-all duration-300
                fixed lg:relative h-screen z-50 lg:z-auto shadow-xl
                ${isCollapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'}
            `}>
                {/* Header with Toggle */}
                <div className="p-3 lg:p-4 text-center border-b border-blue-700/50">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className={`w-full p-2 rounded-lg hover:bg-blue-700/50 transition-colors ${isCollapsed ? 'flex flex-col items-center justify-center' : 'flex flex-col'}`}
                        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <div className={`flex items-center ${isCollapsed ? 'w-full justify-center' : 'w-full flex-col'}`}>
                            <div className={`mx-auto bg-blue-600 rounded-xl flex items-center justify-center shadow-lg ${isCollapsed ? 'w-8 h-8' : 'w-9 h-9 mb-2'}`}>
                                <I.Building2 className={`text-white ${isCollapsed ? 'w-4 h-4' : 'w-5 h-5'}`} />
                            </div>
                            {!isCollapsed && (
                                <h2 className="text-lg font-bold text-white">AP Automate</h2>
                            )}
                        </div>

                        {/* Toggle Icon - Always visible */}
                        <div className={`flex items-center justify-center ${isCollapsed ? 'mt-1' : 'mt-1'}`}>
                            {isCollapsed ? (
                                <I.ChevronRight className="w-4 h-4 text-blue-300" />
                            ) : (
                                <I.ChevronLeft className="w-4 h-4 text-blue-300" />
                            )}
                        </div>
                    </button>
                </div>

                {/* Navigation */}
                <nav className={`flex-1 p-4 space-y-2 ${isCollapsed ? '' : 'overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-blue-700/50 [&::-webkit-scrollbar-track]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-blue-600/80'}`}>
                    {filteredNavItems.map(item => {
                        const hasSubItems = item.subItems && item.subItems.length > 0;
                        const isExpanded = isMenuExpanded(item.id);
                        const isHovered = hoveredMenu === item.id;
                        const isActive = currentPage === item.id || (hasSubItems && item.subItems.some(subItem => currentPage === subItem.id));

                        return (
                            <div key={item.id} className="relative">
                                <button
                                    onClick={() => {
                                        if (hasSubItems) {
                                            if (isCollapsed) {
                                                navigate('tds-invoices');
                                            } else {
                                                toggleMenu(item.id);
                                            }
                                        } else {
                                            navigate(item.id);
                                        }
                                    }}
                                    onMouseEnter={() => hasSubItems && isCollapsed && setHoveredMenu(item.id)}
                                    className={`
                                        w-full flex items-center px-3 py-3 rounded-lg transition-all duration-200
                                        ${isActive
                                            ? 'bg-blue-600 text-white shadow-lg ring-2 ring-blue-400/50'
                                            : 'hover:bg-blue-700/50 hover:text-white'
                                        }
                                        ${isCollapsed ? 'justify-center px-2' : ''}
                                    `}
                                    title={isCollapsed ? item.label : ''}
                                >
                                    <item.icon className={`w-5 h-5 ${isCollapsed ? '' : 'mr-3'} ${isActive ? 'text-white' : ''}`} />
                                    {!isCollapsed && (
                                        <>
                                            <span className="font-medium flex-1 text-left">{item.label}</span>
                                            {hasSubItems && (
                                                <I.ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                            )}
                                        </>
                                    )}
                                </button>

                                {/* Sub-items - Normal expanded state */}
                                {hasSubItems && !isCollapsed && isExpanded && (
                                    <div className="ml-8 mt-1 space-y-1">
                                        {item.subItems.map(subItem => {
                                            const isSubActive = currentPage === subItem.id;
                                            return (
                                                <button
                                                    key={subItem.id}
                                                    onClick={() => navigate(subItem.id)}
                                                    className={`
                                                        w-full flex items-center px-3 py-2 rounded-lg transition-all duration-200 text-sm
                                                        ${isSubActive
                                                            ? 'bg-blue-500 text-white'
                                                            : 'hover:bg-blue-700/30 hover:text-white text-gray-300'
                                                        }
                                                    `}
                                                    title={isCollapsed ? subItem.label : ''}
                                                >
                                                    <subItem.icon className={`w-4 h-4 mr-2 ${isSubActive ? 'text-white' : ''}`} />
                                                    <span className="font-medium">{subItem.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Sub-items - Pop-out for collapsed state */}
                                {hasSubItems && isCollapsed && isHovered && (
                                    <div
                                        className="absolute left-full top-0 ml-2 bg-blue-900 rounded-lg shadow-xl border border-blue-700 min-w-[200px] z-50"
                                        onMouseEnter={() => setHoveredMenu(item.id)}
                                        onMouseLeave={() => setHoveredMenu(null)}
                                    >
                                        <div className="p-2">
                                            <div className="text-xs font-semibold text-blue-300 px-3 py-2 mb-1 border-b border-blue-700">
                                                {item.label}
                                            </div>
                                            {item.subItems.map(subItem => {
                                                const isSubActive = currentPage === subItem.id;
                                                return (
                                                    <button
                                                        key={subItem.id}
                                                        onClick={() => {
                                                            navigate(subItem.id);
                                                            setHoveredMenu(null);
                                                        }}
                                                        className={`
                                                            w-full flex items-center px-3 py-2 rounded-md transition-all duration-200 text-sm
                                                            ${isSubActive
                                                                ? 'bg-blue-600 text-white'
                                                                : 'hover:bg-blue-700/50 hover:text-white text-gray-300'
                                                            }
                                                        `}
                                                    >
                                                        <subItem.icon className={`w-4 h-4 mr-3 ${isSubActive ? 'text-white' : ''}`} />
                                                        <span className="font-medium">{subItem.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                {/* User Section & Logout */}
                <div className="p-4 border-t border-blue-700/50">
                    {!isCollapsed && (
                        <div className="text-center mb-4">
                            <p className="text-sm font-semibold text-white truncate">{user?.username || 'User'}</p>
                            <p className="text-xs text-blue-300 truncate">{user?.roles?.join(', ') || 'No roles'}</p>
                        </div>
                    )}

                    <Button
                        onClick={logout}
                        variant="danger"
                        className={`w-full ${isCollapsed ? 'px-2' : ''}`}
                        title={isCollapsed ? 'Logout' : ''}
                    >
                        <I.LogOut className={`w-5 h-5 ${isCollapsed ? '' : 'mr-3'}`} />
                        {!isCollapsed && <span>Logout</span>}
                    </Button>
                </div>

                {/* Mobile Close Button */}
                <div className="lg:hidden p-4 border-t border-blue-700/50">
                    <button
                        onClick={() => setIsCollapsed(true)}
                        className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-blue-700/50 transition-colors"
                    >
                        <I.X className="w-5 h-5 text-blue-300" />
                    </button>
                </div>
            </aside>

            {/* Mobile Menu Button */}
            {isCollapsed && (
                <button
                    onClick={() => setIsCollapsed(false)}
                    className="lg:hidden fixed top-4 left-4 z-50 p-3 bg-blue-900 text-white rounded-lg shadow-lg hover:bg-blue-800 transition-colors"
                >
                    <I.Menu className="w-5 h-5" />
                </button>
            )}
        </>
    );
};

export default Sidebar;
